import { atom, useAtom } from "jotai"
import { icons } from "../assets/icons/icons"
import { SETTINGS } from "../constants"
import { useResolution } from "../hooks/useResolution"
import { useBGM } from "../hooks/useBGM"
import { useSound } from "./SoundHandler"
import { useWalletMenu, WalletMenu } from "./WalletMenu"
import { useEffect, useRef } from "react"
import * as RadixSlider from "@radix-ui/react-slider";
import { motion, AnimatePresence } from "framer-motion"


import svgBtMenuOn from '../assets/bt-menu-on.svg'
import svgBtMenuOff from '../assets/bt-menu-off.svg'
import svgVolumeBar from '../assets/volume-bar.svg'
import { useModal } from "~/core/contexts/ModalContext"
import { MenueModal } from "../modals/menu.modal"
import { LeaderboardModal } from "../modals/leaderboard.modal"
import { AnnouncementModal, useAnnouncement } from "../modals/annnouncement.modal"

export const Menu = () => {
  const walletMenu = useWalletMenu()
  const sound = useSound()
  const { showModal } = useModal()
  const { isMobile } = useResolution()
  const { hasNew, sync } = useAnnouncement()

  const onWallet = () => {
    setTimeout(() => {
      walletMenu.toggle()
    }, 0)
    sound.play('click');
  }
  
  const onLeaderboard = () => {
    sound.play('click');
    showModal(hide => <LeaderboardModal onDismiss={hide} />, { hideOnEscapeKey: true })
  }

  const onMenu = () => {
    sound.play('click');
    showModal(hide => <MenueModal onDismiss={hide} />, { hideOnEscapeKey: true })
  }

  const onAnnouncement = () => {
    sound.play('click');
    sync()
    showModal(hide => <AnnouncementModal onDismiss={hide} />, { hideOnEscapeKey: true })
  }

  if (isMobile) {
    return (
      <div className="flex">
        <MenuButton onPointerDown={onLeaderboard}>
          <img src={icons.cup} className="w-5" />
        </MenuButton>

        <div className="flex-1 relative">
          {/* <img src={svgLogo} className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2  h-14' /> */}
        </div>

        <MenuButton onPointerDown={onMenu}>
          <img src={icons.menuHamburger} />
        </MenuButton>
      </div>
    )
  }

  return (
    <div>
      <div className='flex'>
        <div className="relative">
          <MenuButton onPointerDown={onWallet}>
            <img src={icons.wallet} />
          </MenuButton>
          <div className="absolute top-0 right-[100%] mr-2 z-30">
            <WalletMenu />
          </div>
        </div>
        <MenuButton onPointerDown={onAnnouncement}>
          <img src={icons.bell} />
          {hasNew && <div className="absolute top-1 right-1 rounded-full w-4 h-4 bg-yellow-500" />}
        </MenuButton>
        <MenuButton onPointerDown={onMenu}>
          <img src={icons.menuHamburger} />
        </MenuButton>
      </div>
      <div className='flex' style={{ transform: 'translate(29px, -10px)' }}>
        <BGMButton />
        <SoundButton />
      </div>
    </div>
  )
}

const MenuButton = ({ children, onPointerDown }: { children: any, onPointerDown: any }) => {
   return (
    <button className="relative group" onPointerDown={onPointerDown}>
      <img 
        src={svgBtMenuOff} 
        className="w-11 h-11 md:w-14 md:h-14 transition-opacity duration-100 group-hover:opacity-0" 
      />
      <img 
        src={svgBtMenuOn} 
        className="absolute inset-0 w-11 h-11 md:w-14 md:h-14 opacity-0 transition-opacity duration-100 group-hover:opacity-100" 
      />
      <div className="absolute inset-0 flex items-center justify-center z-10">
        {children}
      </div>
    </button>
   )
}


const atomShowBGMPopover = atom(false)

export const BGMButton = () => {
  const sound = useSound()

  const [showPopover, setShowPopover] = useAtom(atomShowBGMPopover)

  const onClick = () => {
    setTimeout(() => {
      setShowPopover(!showPopover)
    }, 0);
    sound.play('click');
  }

  return (
    <div className="relative">
      <MenuButton onPointerDown={onClick}>
        <img
          src={icons.music} 
        />
      </MenuButton>
      <BGMPopover />
    </div>
  )
}

const BGMPopover = () => {
  const { volume, setVolume } = useBGM()

  const [showPopover, setShowPopover] = useAtom(atomShowBGMPopover)

  const handleOutsideClickRef = useRef<HTMLDivElement>(null)
  const handleOutsideClick = (event: PointerEvent) => {
    if (handleOutsideClickRef.current && !handleOutsideClickRef.current.contains(event.target as Node)) {
      setShowPopover(false)
    }
  }

  useEffect(() => {
    document.addEventListener('pointerdown', handleOutsideClick)
    return () => document.removeEventListener('pointerdown', handleOutsideClick)
  }, [])

  const onChange = (value: number) => {
    setVolume(value / SETTINGS.MAX_VOLUME)
  }

  return (
    <div className="absolute top-[100%] left-1/2 -translate-x-1/2 mt-2 z-30 w-[38px]">
      <AnimatePresence>
        {showPopover ? (
          <motion.div
            ref={handleOutsideClickRef}
            key="popover"
            initial={{ y: '-20%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 500 }}
          >
            <div 
              ref={handleOutsideClickRef}
              className="relative"
            >
              <img src={svgVolumeBar} className="w-full" />

              <div className="absolute inset-0 flex justify-center items-center">
                <RadixSlider.Root 
                  orientation="vertical"
                  className="relative flex flex-col items-center select-none touch-none w-4 h-[80px]"
                  value={[volume * SETTINGS.MAX_VOLUME]}
                  min={SETTINGS.MIN_VOLUME}
                  max={SETTINGS.MAX_VOLUME}
                  onValueChange={values => onChange(values[0])}
                >
                  <RadixSlider.Track className="relative flex-grow w-full h-full">
                    <div className="absolute left-1/2 -translate-x-1/2 w-1.5 h-[80px] bg-white/10 rounded-full" />
                    <RadixSlider.Range className="absolute bg-[#FDC700] rounded-full w-1 left-1/2 -translate-x-1/2" />
                  </RadixSlider.Track>

                  <RadixSlider.Thumb className="relative block w-5 h-5 focus:outline-none">
                    <img src={icons.paw} className="w-full h-full" />
                  </RadixSlider.Thumb>
                </RadixSlider.Root>
              </div>
            </div>
          </motion.div>
        ) : (
          <>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

const atomShowSoundPopover = atom(false)

export const SoundButton = () => {
  const sound = useSound()

  const [showPopover, setShowPopover] = useAtom(atomShowSoundPopover)

  const onClick = () => {
    setTimeout(() => {
      setShowPopover(!showPopover)
    }, 0);
    sound.play('click');
  }

  const volume = sound.sfxVolume * SETTINGS.MAX_VOLUME

  return (
    <div className="relative">
      <MenuButton onPointerDown={onClick}>
        <img
          src={volume === SETTINGS.MAX_VOLUME ? icons.volumeFull : volume === SETTINGS.MIN_VOLUME ? icons.volumeOff : icons.volumeLow} 
        />
      </MenuButton>
      <SoundPopover />
    </div>
  )
}

const SoundPopover = () => {
  const sound = useSound()

  const [showPopover, setShowPopover] = useAtom(atomShowSoundPopover)

  const handleOutsideClickRef = useRef<HTMLDivElement>(null)
  const handleOutsideClick = (event: PointerEvent) => {
    if (handleOutsideClickRef.current && !handleOutsideClickRef.current.contains(event.target as Node)) {
      setShowPopover(false)
    }
  }

  useEffect(() => {
    document.addEventListener('pointerdown', handleOutsideClick)
    return () => document.removeEventListener('pointerdown', handleOutsideClick)
  }, [])

  const onChange = (value: number) => {
    sound.setSfxVolume(value / SETTINGS.MAX_VOLUME)
  }

  return (
    <div className="absolute top-[100%] left-1/2 -translate-x-1/2 mt-2 z-30 w-[38px]">
      <AnimatePresence>
        {showPopover ? (
          <motion.div
            ref={handleOutsideClickRef}
            key="popover"
            initial={{ y: '-20%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 500 }}
          >
            <div 
              ref={handleOutsideClickRef}
              className="relative"
            >
              <img src={svgVolumeBar} className="w-full" />

              <div className="absolute inset-0 flex justify-center items-center">
                <RadixSlider.Root 
                  orientation="vertical"
                  className="relative flex flex-col items-center select-none touch-none w-4 h-[80px]"
                  value={[sound.sfxVolume * SETTINGS.MAX_VOLUME]}
                  min={SETTINGS.MIN_VOLUME}
                  max={SETTINGS.MAX_VOLUME}
                  onValueChange={values => onChange(values[0])}
                >
                  <RadixSlider.Track className="relative flex-grow w-full h-full">
                    <div className="absolute left-1/2 -translate-x-1/2 w-1.5 h-[80px] bg-white/10 rounded-full" />
                    <RadixSlider.Range className="absolute bg-[#FDC700] rounded-full w-1 left-1/2 -translate-x-1/2" />
                  </RadixSlider.Track>

                  <RadixSlider.Thumb className="relative block w-5 h-5 focus:outline-none">
                    <img src={icons.paw} className="w-full h-full" />
                  </RadixSlider.Thumb>
                </RadixSlider.Root>
              </div>
            </div>
          </motion.div>
        ) : (
          <>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
